import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Field } from 'redux-form';
import { connect } from 'react-redux';
import cn from 'classnames';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import AutocompleteSelectField from 'chameleon/ui-stack/ui/forms/autocomplete-select-field';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import Context from '../../Context';
import FieldWrapper from '../fields/FieldWrapper';
import { FIELD_META_MAP } from '../../utils/constants';
import { getFieldOption } from '../../utils';

import './styles.scss';
import { QuestionTypes } from 'chameleon/src/constants/types';

const AdvancedOptions = ({
  spacingAroundContent = null,
  hasScramblePerStudent,
  hasImmediateFeedbackFF,
}) => {
  const {
    isAssign,
    studentScoringConditions,
    studentScoring,
    showRenameDropdown,
    showScrambleQuestionsOption,
    hasScrambledOrder,
    disableScrambleQuestionsOption,
    showLdbOption,
    isSecure,
    hasImmediateFeedback,
    immediateFeedbackAttempts,
    isFormatPrint,
    secureSelectDisabled,
    advancedSettingsOpenedDefault,
    isPPC,
    assessmentWithQuestionsData,
  } = useContext(Context);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  useEffect(() => {
    if (advancedSettingsOpenedDefault && !showAdvancedSettings) {
      setShowAdvancedSettings(advancedSettingsOpenedDefault);
    }
  }, [advancedSettingsOpenedDefault]);

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  const hasMcqQuestion = (questions) => {
    return questions.some(
      (question) => question.type === QuestionTypes.MULTIPLE_CHOICE_QUESTION,
    );
  };

  const hasScrambledOrderSelectedOption = hasScramblePerStudent
    ? getFieldOption(
        FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT,
        hasScrambledOrder,
        isFormatPrint,
      )
    : getFieldOption(
        FIELD_META_MAP.SCRAMBLE_QUESTIONS,
        hasScrambledOrder,
        isFormatPrint,
      );

  return (
    <Fragment>
      <A11y>
        <div
          className="flex-row justify-between items-center border-t border-gray-300 cursor-pointer mt-4.8 my-4.2 px-12.2 pt-4.2"
          role="button"
          tabIndex={0}
          onClick={toggleAdvancedSettings}
          aria-expanded={showAdvancedSettings}
        >
          <div className="flex-row items-center space-x-4">
            <Icon id="cog" />
            <span className="margin-l-12 text-14">Advanced Settings</span>
          </div>
          <div className="flex-row items-center">
            <Icon
              id="selectbox_arrow_down"
              className={{ 'transform rotate-180': showAdvancedSettings }}
            />
          </div>
        </div>
      </A11y>
      {showAdvancedSettings && (
        <div
          className={cn(
            'ft-assign-manage-form-container border-t border-gray-300 mb-4.8',
            spacingAroundContent,
          )}
        >
          <div className="ft-amf-advanced-settings-container">
            {(studentScoringConditions || isAssign) && (
              <div>
                {studentScoringConditions && (
                  <div className="ft-amf-option-container">
                    <FieldWrapper field={FIELD_META_MAP.STUDENT_SCORING}>
                      <Field
                        name="studentScoring"
                        component={AutocompleteSelectField}
                        props={{
                          className: 'ft-amf-select-root',
                          portalContainerClassName: 'z-overModals',
                          options: FIELD_META_MAP.STUDENT_SCORING.options,
                          value: getFieldOption(
                            FIELD_META_MAP.STUDENT_SCORING,
                            studentScoring,
                          ),
                          placeholder: {
                            label: FIELD_META_MAP.STUDENT_SCORING.placeholder,
                          },
                        }}
                      />
                    </FieldWrapper>
                    {studentScoring && (
                      <div className="ft-amf-option-details">
                        {FIELD_META_MAP.STUDENT_SCORING.detailsComponent()}
                      </div>
                    )}
                  </div>
                )}
                {isAssign && (
                  <Fragment>
                    {showRenameDropdown && (
                      <div
                        className="ft-amf-option-container"
                        data-test-id={FIELD_META_MAP.CUSTOM_NAME.dataTestId}
                      >
                        <FieldWrapper field={FIELD_META_MAP.CUSTOM_NAME}>
                          <Field
                            name="title"
                            component="input"
                            props={{
                              className:
                                'h-12.2 w-full px-4.2 py-0.8 text-14 text-gray-900 border rounded-24.8 border-gray-500',
                              placeholder: 'Enter Custom Name',
                              disabled: false,
                            }}
                          />
                        </FieldWrapper>
                      </div>
                    )}
                    {showScrambleQuestionsOption && (
                      <div
                        className="ft-amf-option-container"
                        data-test-id={
                          FIELD_META_MAP.SCRAMBLE_QUESTIONS.dataTestId
                        }
                      >
                        <FieldWrapper
                          field={
                            hasScramblePerStudent
                              ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT
                              : FIELD_META_MAP.SCRAMBLE_QUESTIONS
                          }
                        >
                          <Field
                            name="hasScrambledOrder"
                            component={AutocompleteSelectField}
                            props={{
                              className: 'ft-amf-select-root',
                              portalContainerClassName: 'z-overModals',
                              options: hasScramblePerStudent
                                ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.options(
                                    isFormatPrint,
                                  )
                                : FIELD_META_MAP.SCRAMBLE_QUESTIONS.options,
                              value: hasScrambledOrderSelectedOption,
                              placeholder: {
                                label:
                                  FIELD_META_MAP.SCRAMBLE_QUESTIONS.placeholder,
                              },
                              disableDropdown: disableScrambleQuestionsOption,
                            }}
                          />
                        </FieldWrapper>
                        <div
                          className="ft-amf-option-details text-gray-600"
                          aria-live="polite"
                        >
                          {hasScramblePerStudent && isPPC
                            ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.detailsComponent(
                                hasScrambledOrderSelectedOption,
                              )
                            : null}
                        </div>
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            )}
            <div className="ft-amf-ldb-container">
              {showLdbOption && (
                <div
                  className="ft-amf-option-container"
                  data-test-id={FIELD_META_MAP.LOCKDOWN_BROWSER.dataTestId}
                >
                  <FieldWrapper field={FIELD_META_MAP.LOCKDOWN_BROWSER}>
                    <Field
                      name="isSecure"
                      component={AutocompleteSelectField}
                      props={{
                        className: 'ft-amf-select-root',
                        portalContainerClassName: 'z-overModals',
                        options: FIELD_META_MAP.LOCKDOWN_BROWSER.options,
                        value: getFieldOption(
                          FIELD_META_MAP.LOCKDOWN_BROWSER,
                          isSecure,
                        ),
                        placeholder: {
                          label: FIELD_META_MAP.LOCKDOWN_BROWSER.placeholder,
                        },
                        disableDropdown: secureSelectDisabled,
                      }}
                    />
                  </FieldWrapper>
                  {isSecure && (
                    <div
                      id={FIELD_META_MAP.LOCKDOWN_BROWSER.ariaLabelledBy}
                      className="ft-amf-option-details"
                      aria-label={FIELD_META_MAP.LOCKDOWN_BROWSER.ariaLabel}
                      role="region"
                      aria-live="polite"
                    >
                      {FIELD_META_MAP.LOCKDOWN_BROWSER.detailsComponent()}
                    </div>
                  )}
                </div>
              )}
              {hasImmediateFeedbackFF &&
                !isFormatPrint &&
                hasMcqQuestion(assessmentWithQuestionsData.questions) && (
                  <div
                    className="ft-amf-option-container"
                    data-test-id={FIELD_META_MAP.IMMEDIATE_FEEDBACK.dataTestId}
                  >
                    <FieldWrapper field={FIELD_META_MAP.IMMEDIATE_FEEDBACK}>
                      <Field
                        name="hasImmediateFeedback"
                        component={AutocompleteSelectField}
                        props={{
                          className: 'ft-amf-select-root',
                          portalContainerClassName: 'z-overModals',
                          options: FIELD_META_MAP.IMMEDIATE_FEEDBACK.options,
                          value: getFieldOption(
                            FIELD_META_MAP.IMMEDIATE_FEEDBACK,
                            hasImmediateFeedback,
                          ),
                        }}
                        disableDropdown={!isAssign}
                      />
                    </FieldWrapper>
                    {hasImmediateFeedback && (
                      <div
                        className="ft-amf-option-details"
                        data-test-id={
                          FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS.dataTestId
                        }
                        role="region"
                        aria-live="polite"
                      >
                        <FieldWrapper
                          field={FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS}
                        >
                          <Field
                            name="immediateFeedbackAttempts"
                            component={AutocompleteSelectField}
                            props={{
                              className: 'ft-amf-select-root',
                              portalContainerClassName: 'z-overModals',
                              options:
                                FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS
                                  .options,
                              value: getFieldOption(
                                FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS,
                                immediateFeedbackAttempts,
                              ),
                            }}
                            disableDropdown={!isAssign}
                          />
                        </FieldWrapper>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

AdvancedOptions.propTypes = {};

function mapStateToProps({ featureFlag }) {
  return {
    hasScramblePerStudent: featureFlag.scramble_questions_per_student,
    hasImmediateFeedbackFF: featureFlag.immediate_feedback,
  };
}

export default connect(mapStateToProps)(AdvancedOptions);
